import { createApi } from "@reduxjs/toolkit/query/react";
import { CouponFormValues, CouponList } from "interfaces/Revenue";
import { customBaseQuery, handleQueryLifecycle } from "store/utils";

type Id = string | number;

export const couponApi = createApi({
  reducerPath: "couponApi",
  baseQuery: customBaseQuery,
  tagTypes: ["Coupons", "Coupon"],
  endpoints: (builder) => ({
    getCoupons: builder.query<any, any>({
      query: (params) => ({ url: "/api/revenue/coupons/", params }),
      // keepUnusedDataFor: 0,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data?.map((item: CouponList) => ({
                type: "Coupon" as const,
                id: item.id,
              })),
              { type: "Coupons" as const, id: "LIST" },
            ]
          : [{ type: "Coupons" as const, id: "LIST" }],
    }),

    getCouponById: builder.query<any, string | number>({
      query: (id) => `/api/revenue/coupons/${id}/`,
      providesTags: (_res, _err, id) => [{ type: "Coupon", id }],
    }),

    createCoupon: builder.mutation<any, Partial<CouponFormValues>>({
      query: (body) => ({
        url: "/api/revenue/coupons/",
        method: "POST",
        body,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "Coupons", id: "LIST" }] : [],
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),

    updateCoupon: builder.mutation<
      any,
      { id: Id; body: Partial<CouponFormValues> }
    >({
      query: ({ id, body }) => ({
        url: `/api/revenue/coupons/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, { id }) =>
        result
          ? [
              { type: "Coupon", id },
              { type: "Coupons", id: "LIST" },
            ]
          : [],
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),

    deleteCoupon: builder.mutation<any, Id>({
      query: (id) => ({
        url: `/api/revenue/coupons/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "Coupons", id: "LIST" }] : [],
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),
  }),
});

export const {
  useGetCouponsQuery,
  useGetCouponByIdQuery,
  useCreateCouponMutation,
  useUpdateCouponMutation,
  useDeleteCouponMutation,
} = couponApi;
